import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { Badge, Button, Input, Label, Skeleton, type MapWorkbenchRuntimeSurfaceSectionRenderArgs } from '@workspace/ui';

import { readRuntimeSurfaceSectionComponentId } from '@/components/mapping/runtimeSurfaceRenderers';
import { useNavigation } from '@/context/NavigationContext';
import {
  buildSalesRequestWizardRoute,
  previewSalesRoute,
  type SalesRequestWizardDraft,
  type SalesRoutePreviewResponse,
} from '@/lib/sales-routing';
import type { SalesWorkspaceContext } from '@/lib/sales-collaboration';
import { useSalesLocationSummary } from './useSalesLocationSummary';

export const SALES_MAP_ROUTING_QUOTE_COMPONENT_ID = 'sales_location_routing_quote';
const PORTAL_DEBUG_LOGS_ENABLED = import.meta.env.DEV && import.meta.env.MODE !== 'test';

type SalesMapRoutingContextValue = {
  salesContext: SalesWorkspaceContext | null;
  requestEnabled: boolean;
  setRuntimeOverlayGeoJSON: (value: Record<string, unknown> | null) => void;
};

export const SalesMapRoutingContext = createContext<SalesMapRoutingContextValue | null>(null);

const logSalesMapRoutingDebug = (message: string, details?: Record<string, unknown>) => {
  if (!PORTAL_DEBUG_LOGS_ENABLED || typeof console === 'undefined' || typeof console.debug !== 'function') {
    return;
  }
  if (details) {
    console.debug(`[SalesMap][Routing] ${message}`, details);
    return;
  }
  console.debug(`[SalesMap][Routing] ${message}`);
};

const summarizeRouteOverlay = (value: Record<string, unknown> | null) => {
  const features = Array.isArray(value?.features) ? (value.features as Array<Record<string, unknown>>) : [];
  return {
    featureCount: features.length,
    geometryTypes: features.map(feature => {
      const geometry = feature.geometry;
      return geometry && typeof geometry === 'object' && typeof (geometry as { type?: unknown }).type === 'string'
        ? (geometry as { type: string }).type
        : 'unknown';
    }),
    roles: features.map(feature =>
      feature.properties && typeof feature.properties === 'object' && typeof (feature.properties as { role?: unknown }).role === 'string'
        ? (feature.properties as { role: string }).role
        : 'unknown',
    ),
  };
};

export function SalesMapRoutingProvider({
  salesContext,
  requestEnabled,
  onRuntimeOverlayGeoJSONChange,
  children,
}: {
  salesContext?: SalesWorkspaceContext | null;
  requestEnabled?: boolean;
  onRuntimeOverlayGeoJSONChange: (value: Record<string, unknown> | null) => void;
  children: ReactNode;
}) {
  const setRuntimeOverlayGeoJSON = useCallback(
    (value: Record<string, unknown> | null) => {
      logSalesMapRoutingDebug('setRuntimeOverlayGeoJSON', {
        overlay: summarizeRouteOverlay(value),
      });
      onRuntimeOverlayGeoJSONChange(value);
    },
    [onRuntimeOverlayGeoJSONChange],
  );

  const value = useMemo<SalesMapRoutingContextValue>(
    () => ({
      salesContext: salesContext ?? null,
      requestEnabled: requestEnabled ?? false,
      setRuntimeOverlayGeoJSON,
    }),
    [requestEnabled, salesContext, setRuntimeOverlayGeoJSON],
  );

  return <SalesMapRoutingContext.Provider value={value}>{children}</SalesMapRoutingContext.Provider>;
}

const readString = (value: unknown) => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

export const isSalesMapRoutingQuoteSection = (section: MapWorkbenchRuntimeSurfaceSectionRenderArgs['section']) =>
  section.kind === 'custom' && readRuntimeSurfaceSectionComponentId(section) === SALES_MAP_ROUTING_QUOTE_COMPONENT_ID;

const formatFeet = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value).toLocaleString()} ft` : '—';

const formatMeters = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value).toLocaleString()} m` : '—';

const formatRoutingProfile = (value?: string | null) =>
  value === 'fiber_construction' ? 'Fiber Construction' : value === 'standard' || !value ? 'Standard' : value;

const buildRouteOverlay = ({
  featureLongitude,
  featureLatitude,
  response,
}: {
  featureLongitude: number | null;
  featureLatitude: number | null;
  response: SalesRoutePreviewResponse;
}) => {
  const features: Array<Record<string, unknown>> = [];
  if (response.route_geometry) {
    features.push({
      type: 'Feature',
      properties: { role: 'route' },
      geometry: response.route_geometry,
    });
  }
  if (featureLongitude != null && featureLatitude != null) {
    features.push({
      type: 'Feature',
      properties: { role: 'origin' },
      geometry: {
        type: 'Point',
        coordinates: [featureLongitude, featureLatitude],
      },
    });
  }
  if (response.nearest_fiber_access_point) {
    features.push({
      type: 'Feature',
      properties: { role: 'access_point' },
      geometry: {
        type: 'Point',
        coordinates: [
          response.nearest_fiber_access_point.longitude,
          response.nearest_fiber_access_point.latitude,
        ],
      },
    });
  }
  return {
    type: 'FeatureCollection',
    features,
  } satisfies Record<string, unknown>;
};

const buildSalesRequestDraft = (
  feature: MapWorkbenchRuntimeSurfaceSectionRenderArgs['feature'],
  response: SalesRoutePreviewResponse,
  salesContext: SalesWorkspaceContext | null,
  requestedBuildType?: string,
): SalesRequestWizardDraft | null => {
  if (
    feature.geometryType !== 'Point' ||
    !Array.isArray(feature.geometry?.coordinates) ||
    feature.geometry.coordinates.length < 2 ||
    typeof feature.geometry.coordinates[0] !== 'number' ||
    typeof feature.geometry.coordinates[1] !== 'number'
  ) {
    return null;
  }
  const properties = feature.properties ?? {};
  const featureIdentityKey = readString(properties.feature_identity_key);
  if (!featureIdentityKey) {
    return null;
  }
  return {
    location: {
      feature_identity_key: featureIdentityKey,
      location_id: readString(properties.location_id) ?? undefined,
      display_name: readString(properties.display_name) ?? undefined,
      address_line1: readString(properties.address_line1) ?? undefined,
      locality: readString(properties.locality) ?? undefined,
      region_code: readString(properties.region_code) ?? undefined,
      postal_code: readString(properties.postal_code) ?? readString(properties.postcode) ?? undefined,
      occupancy_kind: readString(properties.occupancy_kind) ?? undefined,
      business_name: readString(properties.business_name) ?? undefined,
      latitude: feature.geometry.coordinates[1],
      longitude: feature.geometry.coordinates[0],
      metadata: {
        source: feature.source,
        source_layer: feature.sourceLayer,
      },
    },
    sales_context: salesContext,
    route_preview: {
      origin_approach_distance_ft: response.origin_approach_distance_ft,
      origin_approach_distance_m: response.origin_approach_distance_m,
      road_distance_ft: response.road_distance_ft,
      road_distance_m: response.road_distance_m,
      destination_approach_distance_ft: response.destination_approach_distance_ft,
      destination_approach_distance_m: response.destination_approach_distance_m,
      total_build_distance_ft: response.total_build_distance_ft,
      total_build_distance_m: response.total_build_distance_m,
      nearest_fiber_access_point_id: response.nearest_fiber_access_point_id,
      nearest_fiber_access_point: response.nearest_fiber_access_point ?? undefined,
      distance_quality: response.distance_quality,
      source: response.source,
      routing_profile: response.routing_profile,
      route: response.route ?? undefined,
      route_geometry: response.route_geometry ?? undefined,
      preliminary_estimate: response.preliminary_estimate ?? undefined,
      build_type: response.preliminary_estimate?.build_type ?? requestedBuildType ?? undefined,
      metadata: response.metadata ?? undefined,
    },
  };
};

function SalesMapRoutingQuoteSectionBody({ feature }: MapWorkbenchRuntimeSurfaceSectionRenderArgs) {
  const routingContext = useContext(SalesMapRoutingContext);
  const { navigate } = useNavigation();
  const { summary } = useSalesLocationSummary(feature, routingContext?.salesContext);
  const [response, setResponse] = useState<SalesRoutePreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [launchingRequest, setLaunchingRequest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [buildType, setBuildType] = useState(() => readString((feature.properties ?? {}).build_type) ?? '');

  const properties = feature.properties ?? {};
  const featureIdentityKey = readString(properties.feature_identity_key);
  const featureLongitude =
    feature.geometryType === 'Point' &&
    Array.isArray(feature.geometry?.coordinates) &&
    feature.geometry.coordinates.length >= 2 &&
    typeof feature.geometry.coordinates[0] === 'number'
      ? feature.geometry.coordinates[0]
      : null;
  const featureLatitude =
    feature.geometryType === 'Point' &&
    Array.isArray(feature.geometry?.coordinates) &&
    feature.geometry.coordinates.length >= 2 &&
    typeof feature.geometry.coordinates[1] === 'number'
      ? feature.geometry.coordinates[1]
      : null;
  const featureAddress = readString(properties.display_name) ?? readString(properties.address_line1) ?? undefined;
  const routingProfile = (readString(properties.routing_profile) as 'standard' | 'fiber_construction' | null) ?? 'standard';
  const salesContext = routingContext?.salesContext ?? null;
  const requestEnabled = routingContext?.requestEnabled ?? false;
  const normalizedBuildType = buildType.trim();

  const refreshRoute = useCallback(async () => {
    logSalesMapRoutingDebug('refreshRoute invoked', {
      featureIdentityKey,
      featureAddress: featureAddress ?? null,
      featureLatitude,
      featureLongitude,
      routingProfile,
      hasRoutingContext: Boolean(routingContext),
    });
    if (!routingContext) {
      logSalesMapRoutingDebug('refreshRoute aborted: missing routing context', {
        featureIdentityKey,
      });
      setError('Sales routing context is unavailable.');
      setResponse(null);
      return;
    }
    if (
      featureLatitude == null ||
      featureLongitude == null
    ) {
      logSalesMapRoutingDebug('refreshRoute aborted: invalid feature coordinates', {
        featureIdentityKey,
        featureLatitude,
        featureLongitude,
      });
      setError('This sales location does not have valid coordinates for route preview.');
      setResponse(null);
      routingContext.setRuntimeOverlayGeoJSON(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      logSalesMapRoutingDebug('previewSalesRoute request started', {
        featureIdentityKey,
        featureAddress: featureAddress ?? null,
        featureLatitude,
        featureLongitude,
        routingProfile,
      });
      const next = await previewSalesRoute({
        latitude: featureLatitude,
        longitude: featureLongitude,
        address: featureAddress,
        routing_profile: routingProfile,
        build_type: normalizedBuildType || undefined,
        catalog_owner_tenant_id: salesContext?.catalog_owner_tenant_id ?? undefined,
        entitlement_id: salesContext?.entitlement_id ?? undefined,
        metadata: featureIdentityKey ? { feature_identity_key: featureIdentityKey } : undefined,
      });
      setResponse(next);
      const overlay = buildRouteOverlay({
        featureLongitude,
        featureLatitude,
        response: next,
      });
      logSalesMapRoutingDebug('previewSalesRoute request succeeded', {
        featureIdentityKey,
        totalBuildDistanceFt: next.total_build_distance_ft,
        totalBuildDistanceM: next.total_build_distance_m,
        roadDistanceFt: next.road_distance_ft ?? null,
        roadDistanceM: next.road_distance_m ?? null,
        nearestAccessPointId: next.nearest_fiber_access_point_id ?? null,
        overlay: summarizeRouteOverlay(overlay),
      });
      routingContext.setRuntimeOverlayGeoJSON(overlay);
    } catch (err) {
      logSalesMapRoutingDebug('previewSalesRoute request failed', {
        featureIdentityKey,
        message: err instanceof Error ? err.message : String(err),
      });
      setResponse(null);
      setError(err instanceof Error ? err.message : 'Failed to preview the nearest access point route.');
      routingContext.setRuntimeOverlayGeoJSON(null);
    } finally {
      setLoading(false);
    }
  }, [
    featureAddress,
    featureIdentityKey,
    featureLatitude,
    featureLongitude,
    routingContext,
    routingProfile,
    normalizedBuildType,
    salesContext?.catalog_owner_tenant_id,
    salesContext?.entitlement_id,
  ]);

  useEffect(() => {
    logSalesMapRoutingDebug('route preview effect triggered', {
      featureIdentityKey,
      featureAddress: featureAddress ?? null,
      featureLatitude,
      featureLongitude,
      routingProfile,
    });
    void refreshRoute();
  }, [featureAddress, featureIdentityKey, featureLatitude, featureLongitude, refreshRoute, routingProfile]);

  useEffect(() => {
    return () => {
      logSalesMapRoutingDebug('route preview cleanup clearing runtime overlay', {
        featureIdentityKey,
      });
      routingContext?.setRuntimeOverlayGeoJSON(null);
    };
  }, [featureIdentityKey, routingContext]);

  const requestDraft = useMemo(
    () => (response ? buildSalesRequestDraft(feature, response, salesContext, normalizedBuildType || undefined) : null),
    [feature, normalizedBuildType, response, salesContext],
  );

  const handleLaunchRequest = useCallback(async () => {
    if (!requestDraft) {
      return;
    }
    setLaunchingRequest(true);
    setError(null);
    try {
      navigate(buildSalesRequestWizardRoute(requestDraft));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to launch the sales request wizard.');
    } finally {
      setLaunchingRequest(false);
    }
  }, [navigate, requestDraft]);

  const quoteSummary = summary?.quote_summary ?? null;

  return (
    <section className="space-y-4">
      {quoteSummary ? (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="muted">{quoteSummary.total_quote_count} total</Badge>
          <Badge variant="outline">{quoteSummary.open_quote_count} open</Badge>
          {quoteSummary.latest_quote_at ? (
            <Badge variant="outline">Latest quote ready</Badge>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-[4.5rem] rounded-lg" />
          <Skeleton className="h-[4.5rem] rounded-lg" />
        </div>
      ) : null}

      {!loading && response ? (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-muted/40 px-3 py-3">
            <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Total build distance</div>
            <div className="mt-1 font-medium text-foreground">{formatFeet(response.total_build_distance_ft)}</div>
            <div className="text-xs text-muted-foreground">{formatMeters(response.total_build_distance_m)}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              Origin {formatFeet(response.origin_approach_distance_ft)} · Roadway {formatFeet(response.road_distance_ft)} · Access {formatFeet(response.destination_approach_distance_ft)}
            </div>
          </div>
          <div className="rounded-lg bg-muted/40 px-3 py-3">
            <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Nearest access point</div>
            <div className="mt-1 truncate font-medium text-foreground">
              {response.nearest_fiber_access_point?.name || response.nearest_fiber_access_point_id || '—'}
            </div>
            <div className="text-xs text-muted-foreground">{response.distance_quality || response.source || '—'}</div>
          </div>
        </div>
      ) : null}

      <div className="space-y-1">
        <Label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Build type</Label>
        <Input
          value={buildType}
          onChange={event => setBuildType(event.target.value)}
          placeholder="Optional, e.g. aerial"
          className="h-8 text-sm"
        />
        <p className="text-xs text-muted-foreground">Required when the active price grid has multiple build-type rates.</p>
      </div>

      {!loading && response ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-muted-foreground">
            Profile: {formatRoutingProfile(response.routing_profile)}{normalizedBuildType ? ` · ${normalizedBuildType}` : ''}
          </div>
          <div className="flex flex-col gap-1 sm:items-end">
            <Button
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => void handleLaunchRequest()}
              disabled={launchingRequest || !requestDraft || !requestEnabled}
            >
              {launchingRequest ? 'Opening…' : 'Start Request'}
            </Button>
            {!requestEnabled ? (
              <p className="text-xs text-muted-foreground">A reseller entitlement is required to create requests for this seller.</p>
            ) : null}
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="rounded-md bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <div>{error}</div>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 h-auto px-0 py-0 text-xs text-destructive hover:bg-transparent hover:text-destructive/80"
            onClick={() => void refreshRoute()}
          >
            Try again
          </Button>
        </div>
      ) : null}
    </section>
  );
}

export function renderSalesMapRoutingQuoteSection(args: MapWorkbenchRuntimeSurfaceSectionRenderArgs) {
  if (!isSalesMapRoutingQuoteSection(args.section)) {
    return null;
  }
  return <SalesMapRoutingQuoteSectionBody {...args} />;
}
